import React, { useState, useEffect, useRef, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import moment from "moment/moment";
import "../pagination/pagination.css";
import Pagination from "../pagination/pagination";
import Select from "react-select";
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import LeaseApiService from "../../services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ClipLoader } from 'react-spinners';
import { toast } from "react-toastify";
import * as XLSX from "xlsx";
import { useTranslation } from 'react-i18next';
import "../customerMaster/customerListing.css";
import "./reports.css";
import { useReportCards } from "../shared/reportCards";

const LeaseMoveInOutReport = () => {
    const { t } = useTranslation(['reports', 'common']);
    const boolOptions = [
        { label: "--", value: "" },
        { label: t('reports:leaseMoveInOut.yes'), value: true },
        { label: t('reports:leaseMoveInOut.no'), value: false },
    ];
    const apiService = new LeaseApiService();
    const gridRef = useRef();
    const [allRows, setAllRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [FilterTab, setFilterTab] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [unitList, setUnitList] = useState([]);
    const [unitClassList, setUnitClassList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [formData, setFormData] = useState({
        property: { label: "All", value: "All" },
        unitNo: "",
        unitClass: { label: "All", value: "All" },
        moveIn: boolOptions[0],
        moveOut: boolOptions[0],
        flatShifting: boolOptions[0],
        from: "",
        to: "",
    })
    const formatDate = (date) => {
        return moment(date).format("DD-MMM-YYYY");
    };
    useEffect(() => {
        fetchDropdownData();

        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setFullYear(today.getFullYear() - 5);
        const toDate = new Date(today);
        toDate.setFullYear(today.getFullYear() + 5);

        setFormData((prev) => ({
            ...prev,
            from: formatDate(fromDate),
            to: formatDate(toDate),
        }));
    }, []);

    const dateFormatter = (params) => {
        if (!params.value) return '';
        const date = moment(params.value);
        return date.isValid() ? date.format('DD-MMM-YYYY') : '';
    };
    const dateComparator = (date1, date2) => {
        const momentDate1 = moment(date1);
        const momentDate2 = moment(date2);
        return momentDate1.isSame(momentDate2) ? 0 : momentDate1.isBefore(momentDate2) ? -1 : 1;
    };

    const fetchDropdownData = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LRAgreeSearchProperties,LRSearchUnitClass,');
            const res = await apiService.getApiParamater('REPORT_MULTI_DROP', `?${params.toString()}`);
            const addAllOption = (list) => [{ value: 'All', text: 'All' }, ...list || []];
            setPropertyList(res?.data?.dropdowns?.LRAgreeSearchProperties);
            setUnitClassList(addAllOption(res?.data?.dropdowns?.UnitClass));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }

    const getItems = async () => {
        try {
            setLoader(true);
            const today = new Date();
            let todate = formatDate(today);
            const oneYearBack = new Date();
            oneYearBack.setFullYear(today.getFullYear() - 1);
            const updatedValue = formatDate(oneYearBack);
            const params = new URLSearchParams();
            params.append("moveInOutRequest.FromDate", formData?.from || updatedValue);
            params.append("moveInOutRequest.ToDate", formData?.to || todate);
            params.append("moveInOutRequest.PropertyNo", formData?.property?.value?.value || "All");
            params.append("moveInOutRequest.UnitNo", formData?.unitNo?.label || "All");
            params.append("moveInOutRequest.UnitClass", formData?.unitClass?.label || "All");
            if (formData?.moveIn?.value !== "") params.append("moveInOutRequest.MoveIn", formData?.moveIn?.value);
            if (formData?.moveOut?.value !== "") params.append("moveInOutRequest.MoveOut", formData?.moveOut?.value);
            if (formData?.flatShifting?.value !== "") params.append("moveInOutRequest.FlatShifting", formData?.flatShifting?.value);
            const res = await apiService.getApiParamater("REPORT_MOVE_IN_OUT", `?${params.toString()}`);
            const result = res?.data?.result || [];
            if (result?.length) {
                setAllRows(result);
                setCurrentPage(1);
            } else {
                setAllRows([]);
                setCurrentPage(1);
                if (res?.data?.message) toast.error(res.data.message);
            }
            setLoader(false);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching items:", error);
            setLoader(false);
            setLoading(false);
        }
    }
    const columnDefs = [
        { headerName: t('reports:leaseMoveInOut.columns.propertyNo'), field: "propertyNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130, pinned: "left" },
        { headerName: t('reports:leaseMoveInOut.columns.propertyName'), field: "propertyName", filter: true, floatingFilter: true, suppressMovable: true, width: 200, pinned: "left" },
        { headerName: t('reports:leaseMoveInOut.columns.unitNo'), field: "unitNo", filter: true, floatingFilter: true, suppressMovable: true, width: 110, pinned: "left" },
        { headerName: t('reports:leaseMoveInOut.columns.tenantNo'), field: "tenantNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: t('reports:leaseMoveInOut.columns.tenantName'), field: "tenantName", filter: true, floatingFilter: true, suppressMovable: true, width: 200 },
        { headerName: t('reports:leaseMoveInOut.columns.agentDirect'), field: "agentDirect", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: t('reports:leaseMoveInOut.columns.contractType'), field: "contractType", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: t('reports:leaseMoveInOut.columns.contractPeriod'), field: "contractPeriod", filter: true, floatingFilter: true, suppressMovable: true, width: 150 },
        { headerName: t('reports:leaseMoveInOut.columns.unitClass'), field: "unitClass", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        { headerName: t('reports:leaseMoveInOut.columns.type'), field: "type", filter: true, floatingFilter: true, suppressMovable: true, width: 120 },
        {
            headerName: t('reports:leaseMoveInOut.columns.actualStartDate'), field: "actualStartDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        {
            headerName: t('reports:leaseMoveInOut.columns.leaseDate'), field: "leaseDate", filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        {
            headerName: t('reports:leaseMoveInOut.columns.vacatingDate'), field: "vacatingDate", filter: true, floatingFilter: true, suppressMovable: true, width: 140,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        { headerName: t('reports:leaseMoveInOut.columns.finishedPreTerminated'), field: "finishedPreTerminated", filter: true, floatingFilter: true, suppressMovable: true, width: 190 },
        { headerName: t('reports:leaseMoveInOut.columns.remarks'), field: "remarks", filter: true, floatingFilter: true, suppressMovable: true, width: 200 },
    ]
    const handleClearFilters = () => {
        setUnitList([]);
        setAllRows([]);
        setCurrentPage(1);
        const today = new Date();
        const fromDate = new Date(today);
        fromDate.setFullYear(today.getFullYear() - 5);
        const toDate = new Date(today);
        toDate.setFullYear(today.getFullYear() + 5);
        setFormData({
            property: { label: "All", value: "All" },
            unitNo: "",
            unitClass: { label: "All", value: "All" },
            moveIn: boolOptions[0],
            moveOut: boolOptions[0],
            flatShifting: boolOptions[0],
            from: formatDate(fromDate),
            to: formatDate(toDate),
        });
    }
    const handleDropDownChange = async ({ name, value }) => {
        if (name === "property") {
            setFormData(prev => ({ ...prev, unitNo: null }));
            const propertyValue = value?.value === 'All' ? null : value?.value;
            const params = new URLSearchParams();
            params.append('DropdownFor', 'LRAgreehUnitNosByPropertyId');
            params.append('CascadValue', propertyValue?.value);
            const res = await apiService.getApiParamater('REPORT_CASCAD', `?${params.toString()}`);
            setUnitList(res?.data?.items || []);
            setFormData(prev => ({ ...prev, [name]: value }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }
    const handleDateChange = async (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;

        const updatedValue = typeof value === 'string' ? value : formatDate(value);
        let updatedDates = { ...formData, [name]: updatedValue };
        setFormData(updatedDates);
    }
    const normalizeValue = (value) => {
        if (value === null || value === undefined) return "";
        if (value === 0 || value === "0") return "";

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return moment(value).format("DD-MMM-YYYY");
        }

        return value;
    };
    const exportColumns = [
        { header: "Property No", dataKey: "propertyNo" },
        { header: "Property Name", dataKey: "propertyName" },
        { header: "Unit No", dataKey: "unitNo" },
        { header: "Tenant No", dataKey: "tenantNo" },
        { header: "Tenant Name", dataKey: "tenantName" },
        { header: "Agent/Direct", dataKey: "agentDirect" },
        { header: "Contract Type", dataKey: "contractType" },
        { header: "Contract Period", dataKey: "contractPeriod" },
        { header: "Unit Class", dataKey: "unitClass" },
        { header: "Type", dataKey: "type" },
        { header: "Actual Start Date", dataKey: "actualStartDate" },
        { header: "Lease Date", dataKey: "leaseDate" },
        { header: "Vacating Date", dataKey: "vacatingDate" },
        { header: "Finished/Pre-Terminated", dataKey: "finishedPreTerminated" },
        { header: "Remarks", dataKey: "remarks" },
    ];
    const exportPDF = () => {
        if (!allRows?.length) return;

        const doc = new jsPDF("landscape", "pt", "A4");

        doc.setFillColor(110, 81, 153);
        doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");

        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text("Lease Move In / Move Out Report", 40, 32);

        doc.setFontSize(9);
        doc.text(
            `Period: ${formData?.from} to ${formData?.to}`,
            doc.internal.pageSize.width - 220,
            32
        );

        doc.setTextColor(0);

        doc.autoTable({
            startY: 60,
            head: [exportColumns.map(col => col.header)],
            body: allRows.map(row =>
                exportColumns.map(col => normalizeValue(row[col.dataKey]))
            ),
            styles: {
                fontSize: 8,
                cellPadding: 5,
                valign: "middle",
            },
            headStyles: {
                fillColor: [110, 81, 153],
                textColor: 255,
                fontStyle: "bold",
                halign: "center",
            },
            bodyStyles: {
                halign: "center",
            },
            alternateRowStyles: {
                fillColor: [240, 237, 245],
            },
            didDrawPage: data => {
                doc.setFontSize(8);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: "center" }
                );
            },
        });
        doc.save("Lease_MoveInOut_Report.pdf");
    };
    const exportExcel = () => {
        if (!allRows?.length) return;

        const excelData = allRows.map(item => {
            const row = {};
            exportColumns.forEach(col => {
                row[col.header] = normalizeValue(item[col.dataKey]);
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Move In Move Out");
        XLSX.writeFile(workbook, "Lease_MoveInOut_Report.xlsx");
    };

    const totalPages = Math.max(1, Math.ceil(allRows.length / pageSize));
    const pagination = {
        currentPage,
        totalPages,
        totalRecords: allRows.length,
    };
    const rowData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return allRows.slice(start, start + pageSize);
    }, [allRows, currentPage, pageSize]);
    const changePage = (pageNo) => {
        const clamped = Math.min(Math.max(pageNo, 1), totalPages);
        setCurrentPage(clamped);
    }

    const cards = useReportCards({
        label: "Movements in view",
        rowData,
        exportData: allRows,
        pagination,
        fields: [["type",2],["propertyName",2]],
    });

    return (
        <div className="cl-page rpt-legacy">
            <div className="cl-titlebar">
                <div className="cl-title">
                    <h1>{t('reports:leaseMoveInOut.title')}</h1>
                    <p>{t('reports:leaseMoveInOut.description', 'Tenant move-in and move-out activity.')}</p>
                </div>
            </div>
            {cards.strip}
            <div className="cost-center-section">
                <div
                    className="d-flex mt-3 cursor-pointer slide-header"
                    onClick={() => setFilterTab(!FilterTab)}
                >
                    <div className="h6 pt-1">{t('common:searchParameters')}</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain ">
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:property')} </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.property}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'property', value: selectedOption })}
                                    options={propertyList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:unitNo')} </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.unitNo}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'unitNo', value: selectedOption })}
                                    options={unitList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('reports:unitClass')}</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.unitClass}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'unitClass', value: selectedOption })}
                                    options={unitClassList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">{t('common:from')}</label>
                                <DatePicker
                                    selected={formData.from}
                                    onChange={(date) => handleDateChange({ name: 'from', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label w-0">{t('common:to')}</label>
                                <DatePicker
                                    selected={formData.to}
                                    onChange={(date) => handleDateChange({ name: 'to', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100 datepicker-icon"
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('reports:moveIn')}</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.moveIn}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'moveIn', value: selectedOption })}
                                    options={boolOptions}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('reports:moveOut')}</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.moveOut}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'moveOut', value: selectedOption })}
                                    options={boolOptions}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('reports:flatShifting')}</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.flatShifting}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'flatShifting', value: selectedOption })}
                                    options={boolOptions}
                                />
                            </div>
                            <div className="d-flex mt-2 gap-1">
                                {loader === true ?
                                    <div className='mainbtnGry flex-end gap-2 w-50'>
                                        <ClipLoader color="#ffffff" className='mt-1' size={25} />
                                    </div>
                                    :
                                    <div
                                        onClick={() => getItems()}
                                        className="mainbtnGry flex-end gap-2 w-50"
                                    >
                                        <FaFileCircleExclamation
                                            color="#fff"
                                            className="center mt-2 mx-2"
                                        />
                                        <div className="center">{t('common:search')}</div>
                                    </div>
                                }
                                <div
                                    onClick={() => handleClearFilters()}
                                    className="mainbtnRed flex-end gap-2 w-50"
                                >
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">{t('common:reset')}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div className="border-box2 p-3">
                {loading ? (
                    <div>{t('common:loading')}</div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between">
                            <h5 className="col mainInvHead">{t('reports:leaseMoveInOut.title')}</h5>
                            <button className="btn btn-primary" onClick={exportPDF}>
                                {t('reports:exportAsPdf')}
                            </button>
                            <button className="btn btn-secondary mx-3" onClick={exportExcel}>
                                {t('reports:exportAsExcel')}
                            </button>
                        </div>

                        <div className="gridshad mt-3">
                            <div className="ag-theme-quartz" style={{ height: 650 }}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={cards.shownRows}
                                    enableFilter={true}
                                    floatingFilter={true}
                                    columnDefs={columnDefs}
                                />
                            </div>
                            {!cards.active && <Pagination
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                pagination={pagination}
                                getItems={changePage}
                            />}
                        </div>
                    </>
                )}
            </div>
        </div >
    );
};
export default LeaseMoveInOutReport;
